import React, { useEffect, useRef, useState } from 'react';
import { AppBar, Toolbar, Box, IconButton, Typography, Badge, useMediaQuery, useTheme, Drawer, Divider } from '@mui/material';
import { Close, Menu as MenuIcon, ShoppingBagOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import ClientSelector from './ClientSelector';
import { useClientContext } from '@/common/application/contexts/ClientContext';
import SidebarList from '@/common/presentation/components/layout/Sidebar/SidebarList';
import SidebarNavUser from '@/common/presentation/components/layout/Sidebar/SidebarList/SidebarNavUser';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { SidebarContext, useSidebar } from '@/common/presentation/components/layout/Sidebar/context';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isSideBarOpen, setIsSideBarOpen } = useSidebar();
  const previousSidebarState = useRef(isSideBarOpen);
  const { getUserName } = useUserPreferences(localStorage.getItem('userName') as string);
  const userName = getUserName();
  const { selectedInvoices, clientLogo, clientLogoLoading } = useClientContext();

  const handleNavigateToSelectedInvoices = () => {
    navigate('/selected-invoices');
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      previousSidebarState.current = isSideBarOpen;
    }
  }, [isSideBarOpen, menuOpen]);

  const handleOpenMenu = () => {
    previousSidebarState.current = isSideBarOpen;
    setIsSideBarOpen(true);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setIsSideBarOpen(previousSidebarState.current);
  };

  return (
    <AppBar
      position="static"
      elevation={1}
      sx={{
        backgroundColor: '#262839',
        color: 'white',
        borderBottom: 'none',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          minHeight: '64px !important',
          padding: isMobile ? '0 8px' : '0 16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          <Logo expanded={!isMobile} clientLogo={clientLogo} loading={clientLogoLoading} />
          {!isMobile && (
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '1.25rem',
              }}
            >
              Customer Portal
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          {/* Client Selector */}
          <ClientSelector />

          {/* Basket Button */}
          <IconButton
            onClick={handleNavigateToSelectedInvoices}
            sx={{
              color: 'white',
              padding: isMobile ? '8px' : '12px',
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            aria-label="Selected Invoices"
          >
            {selectedInvoices?.totalCount && selectedInvoices?.totalCount > 0 ? (
              <Badge
                badgeContent={selectedInvoices?.totalCount ? `+${selectedInvoices.totalCount}` : ''}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#FF4444',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    minWidth: '20px',
                    height: '20px',
                    borderRadius: '10px',
                  },
                }}
              >
                <ShoppingBagOutlined />
              </Badge>
            ) : (
              <ShoppingBagOutlined />
            )}
          </IconButton>
          {/* Menu */}
          {screenWidth < 576 && (
            <IconButton
              onClick={handleOpenMenu}
              sx={{
                color: 'white',
                padding: isMobile ? '8px' : '12px',
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              aria-label="Customer Portal"
            >
              <MenuIcon fontSize="medium" style={{ transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          )}
        </Box>
      </Toolbar>
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={handleCloseMenu}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: screenWidth < 576 ? 'block' : 'none',
        }}
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: 320,
            backgroundColor: '#1A1A2E',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <SidebarContext.Provider value={{ isSideBarOpen: true, setIsSideBarOpen }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Customer Portal
            </Typography>
            <IconButton
              onClick={handleCloseMenu}
              sx={{
                color: 'white',
                padding: '4px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
              aria-label="Close menu"
            >
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <PerfectScrollbar className="disable_ps__rail-x" style={{ height: '100%', padding: '1rem 0' }}>
              <SidebarList />
            </PerfectScrollbar>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          <Box sx={{ p: 2 }}>
            <SidebarNavUser name={userName} forceIsSideBarOpen />
          </Box>
        </SidebarContext.Provider>
      </Drawer>
    </AppBar>
  );
};

export default Header;
